import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { ArtifactLink } from '../components/ArtifactLink';
import { ADDON_UPLOAD_KIND_LABELS, ARTIFACT_TYPE_LABELS, type AddonUploadKind, pageTitle } from '@thottbot/shared';
import { uploadAddonFiles, type UploadOutcome } from '../api/addon-upload';
import { useAddonInfo, useAddonUpload } from '../api/client';
import { LoginGate } from '../components/LoginGate';

const SAVED_PATH = 'World of Warcraft/_classic_beta_/WTF/Account/<ACCOUNT>/SavedVariables/Thottbot.lua';
const WDB_PATH = 'World of Warcraft/_classic_beta_/Cache/WDB/enUS/';
const ADB_PATH = 'Cache/ADB/enUS/DBCache.bin';

/** One chosen file and what the server said about it. */
interface FileResult {
  name: string;
  outcome: UploadOutcome;
}

export function AddonUpload() {
  const info = useAddonInfo();
  const [results, setResults] = useState<FileResult[]>([]);
  return (
    <div className="page" data-testid="addon-upload-page">
      <title>{pageTitle('Upload addon data')}</title>
      <h1>Upload addon data</h1>
      <p>
        Run <code>/reload</code> (or log out) so the game writes the file, then choose{' '}
        <code>{SAVED_PATH}</code>. New to this? Read the <Link to="/addon">addon page</Link> first. On Chrome or Edge,{' '}
        <Link to="/addon/watch">Auto-upload</Link> can do this for you every time the game writes the file.
      </p>
      <p>
        Cache files: <code>{WDB_PATH}questcache.wdb</code>, <code>creaturecache.wdb</code>, <code>gameobjectcache.wdb</code> and{' '}
        <code>{ADB_PATH}</code>. The game writes them while you play and at logout; they hold the quests, creatures, objects
        and item hotfixes your client has seen. Drop them here together with <code>Thottbot.lua</code> or on their own.
      </p>
      <LoginGate action="upload addon data">
        <UploadForm maxBytes={info.data?.maxBytes} onUploaded={setResults} />
        {results.map((r, i) => (
          <FileResultView key={`${i}-${r.name}`} result={r} />
        ))}
      </LoginGate>
    </div>
  );
}

/** The manual form; the upload itself is shared with the folder watcher (api/addon-upload.ts). */
function UploadForm({ maxBytes, onUploaded }: { maxBytes?: number; onUploaded: (results: FileResult[]) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<{ index: number; total: number; percent: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    const files = [...(fileRef.current?.files ?? [])];
    if (!files.length) {
      setError('Choose your Thottbot.lua or cache files first.');
      return;
    }
    setError(null);
    onUploaded([]);
    setProgress({ index: 0, total: files.length, percent: 0 });
    const results: FileResult[] = [];
    await uploadAddonFiles(files, 'manual', {
      onProgress: (index, percent) => setProgress({ index, total: files.length, percent }),
      onResult: (index, outcome) => {
        results.push({ name: files[index]!.name, outcome });
        onUploaded([...results]);
      },
    });
    setProgress(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <form className="section" aria-label="Addon file upload" onSubmit={submit} data-testid="addon-upload-form">
      <div className="form-row">
        <label htmlFor="addon-file">Thottbot.lua or cache files</label>
        <input id="addon-file" ref={fileRef} type="file" accept=".lua,.wdb,.bin" multiple />
      </div>
      {progress !== null && (
        <div className="muted" role="progressbar" aria-valuenow={progress.percent} aria-valuemin={0} aria-valuemax={100}>
          Uploading{progress.total > 1 ? ` file ${progress.index + 1} of ${progress.total}` : ''}... {progress.percent}%
        </div>
      )}
      {error && (
        <div className="notice error" role="alert">
          {error}
        </div>
      )}
      <div className="actions">
        <button type="submit" disabled={progress !== null}>
          Upload
        </button>
        <span className="muted">
          One or more files{maxBytes ? `, each ${Math.round(maxBytes / (1024 * 1024))} MB or smaller` : ''}.
        </span>
      </div>
    </form>
  );
}

const REFUSALS: Record<string, string> = {
  rate_limited: 'Slow down: you have hit the upload limit for now.',
  unauthorized: 'Your session has expired. Log in again and retry.',
};

/** One block per chosen file: the kind the server detected and its summary, or why it was refused. */
function FileResultView({ result }: { result: FileResult }) {
  const { name, outcome } = result;
  if (outcome.kind === 'accepted' || outcome.kind === 'duplicate') {
    const kind: AddonUploadKind = outcome.upload.kind ?? 'addon';
    return (
      <section className="section" data-testid="upload-file-result" data-kind={kind}>
        <h2>
          {ADDON_UPLOAD_KIND_LABELS[kind]} <span className="muted">({name})</span>
        </h2>
        <UploadResult id={outcome.upload.id} duplicate={outcome.kind === 'duplicate'} />
      </section>
    );
  }
  const message = outcome.kind === 'rejected' || outcome.kind === 'error' ? outcome.message : REFUSALS[outcome.kind];
  return (
    <section className="section" data-testid="upload-file-result" data-kind="refused">
      <h2>
        Not uploaded <span className="muted">({name})</span>
      </h2>
      <div className="notice error" role="alert">
        {message}
      </div>
    </section>
  );
}

function UploadResult({ id, duplicate }: { id: string; duplicate: boolean }) {
  const q = useAddonUpload(id);
  if (q.isPending) return <p className="muted">Loading...</p>;
  if (q.isError || !q.data) return <div className="notice error">Could not load the upload status.</div>;
  const u = q.data;
  if (u.status === 'processing') {
    return (
      <p className="muted" role="status" data-testid="addon-upload-status">
        Processing... reading {u.counts.read.npcs} NPCs, {u.counts.read.objects} objects, {u.counts.read.quests} quests and{' '}
        {u.counts.read.items} items into the database.
      </p>
    );
  }
  if (u.status === 'failed') {
    return (
      <div className="notice error" role="alert" data-testid="addon-upload-status">
        Processing failed: {u.error ?? 'unknown error'}. Try again later; if it keeps failing, keep the file and report it.
      </div>
    );
  }
  const read = u.counts.read;
  const art = u.counts.artifacts;
  const merged = u.counts.merged;
  return (
    <div data-testid="addon-upload-summary">
      <h3>Upload {u.status === 'applied' ? 'complete' : u.status}</h3>
      {duplicate && (
        <p className="muted" role="status">
          This exact file was uploaded before; nothing was counted again.
        </p>
      )}
      <div className="table-scroll">
        <table className="grid fields">
          <tbody>
            <tr>
              <th>Records read</th>
              <td>
                {read.npcs} NPCs, {read.objects} objects, {read.quests} quests, {read.items} items, {read.lootEvents} loot
                events, {read.vendors} vendors, {read.trainers} trainers
                {read.skippedTables ? `, ${read.skippedTables} rows of other tables skipped` : ''}
                {read.removedIds ? `, ${read.removedIds} removed items` : ''}
              </td>
            </tr>
            {art && (
              <tr>
                <th>Artifacts</th>
                <td>
                  {art.added} added, {art.updated} updated, {art.skipped} skipped
                  {art.removed ? `, ${art.removed} removed` : ''}
                </td>
              </tr>
            )}
            {merged && (u.kind === 'addon' || !u.kind) && (
              <tr>
                <th>Merged</th>
                <td>
                  {merged.sightings} sightings, {merged.lootEvents} loot events, {merged.vendorItems} vendor items,{' '}
                  {merged.trainerSpells} trainer spells
                </td>
              </tr>
            )}
            <tr>
              <th>{u.kind && u.kind !== 'addon' ? 'Format / build' : 'Addon / build'}</th>
              <td>
                {u.addonVersion} on client {u.clientBuild}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {u.artifacts.length > 0 && (
        <p>
          {u.artifacts.map((a, i) => (
            <span key={`${a.type}/${a.slug}`}>
              {i > 0 && ', '}
              <ArtifactLink type={a.type} slug={a.slug} name={a.name} />{' '}
              <span className="muted">({ARTIFACT_TYPE_LABELS[a.type].singular})</span>
            </span>
          ))}
        </p>
      )}
      <p className="muted">
        This upload is on <Link to="/me">your contributions page</Link>. Upload the same file again after more play to
        replace it.
      </p>
    </div>
  );
}
